import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { ComputePageFlowResponse } from '@fullstory/proto/fs/services/alexa';
import { GetAllPagesResponse, ObjectDeps } from '@fullstory/proto/fs/services/dx-objects';
import { GetAllRequest } from '@fullstory/proto/fs/services/paige';
import { createRequestReducer, DispatchedAction, RequestActionType } from '@fullstory/redux-util';
import { createAppCacheReducer } from '@fullstory/redux-util/utils';
import { combineReducers } from 'redux';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { PagesRequestActions } from './actions';
import { PageRequestTypes, PagesState } from './types';

type State = ReturnType<typeof pagesRequests>;

const depsToState = (deps: ObjectDeps | null, state: State): State => {
  if (!deps) return state;

  const nextPages = new Map(state.data?.pages ?? []);
  const pages = deps.pages;

  for (const [pageId, page] of Object.entries(pages)) {
    if (page.page) {
      nextPages.set(parseInt(pageId, 10), page.page);
    }
  }

  return {
    ...state,
    data: {
      ...state.data,
      pages: nextPages,
      urlMatches: state.data?.urlMatches ?? { urls: [], matches: new Map() },
    },
  };
};

const pagesRequests = createRequestReducer<PagesState, GetAllPagesResponse>({
  mergeData: (state, rsp: GetAllPagesResponse): PagesState => {
    const updated = new Map(state.pages);
    rsp.pages.forEach(page => {
      if (page?.page) {
        updated.set(page.page.id, page.page);
      }
    });
    return {
      pages: updated,
      learnedLimit: rsp.learnedLimit,
      maxPages: rsp.maxPages,
      mlState: rsp.mlState,
      urlMatches: state.urlMatches ?? { urls: [], matches: new Map() },
    };
  },
})(
  PageRequestTypes.GetAllPagesRequest,
  PageRequestTypes.GetAllPagesSuccess,
  PageRequestTypes.GetAllPagesFailure,
);

type pageAction =
  | RequestActionType<typeof PagesRequestActions.setPage>
  | RequestActionType<typeof PagesRequestActions.archivePage>
  | RequestActionType<typeof PagesRequestActions.archivePages>
  | RequestActionType<typeof PagesRequestActions.matchUrls>
  | AppBackendActions
  | DispatchedAction<
      PageRequestTypes.GetAllPagesRequest,
      PageRequestTypes.GetAllPagesSuccess,
      PageRequestTypes.GetAllPagesFailure,
      GetAllRequest,
      GetAllPagesResponse
    >;

const defaultState: State = {
  lastRequested: -1,
  lastResponded: -1,
  loading: false,
  error: false,
  data: {
    pages: new Map(),
    urlMatches: {
      urls: [],
      matches: new Map(),
    },
  },
  safeMessage: null,
};

export const pages = (state = defaultState, action: pageAction): State => {
  switch (action.type) {
    case PageRequestTypes.SetPageSuccess:
    case PageRequestTypes.ArchivePageSuccess: {
      const { page } = action.data;
      if (page?.page?.id) {
        const id = page.page.id;
        const updated: Map<number, PageDefinition> = new Map(state.data?.pages ?? []);
        updated.set(id, page.page);

        return {
          ...state,
          data: {
            ...state.data,
            pages: updated,
            urlMatches: state.data?.urlMatches ?? { urls: [], matches: new Map() },
          },
          safeMessage: null,
        };
      }
      return state;
    }
    case PageRequestTypes.MatchUrlsSuccess: {
      const response = action.data;
      const matches = new Map<string, PageDefinition>();
      const urls = Object.keys(response.urlMatches ?? {});

      for (const url of urls) {
        const match = response.urlMatches?.[url];
        if (match?.matches && match.bestMatchPage) {
          matches.set(url, match.bestMatchPage);
        }
      }

      return {
        ...state,
        data: {
          ...state.data,
          pages: state.data?.pages ?? new Map(),
          urlMatches: {
            urls,
            matches,
          },
        },
      };
    }
    case PageRequestTypes.ArchivePagesSuccess: {
      const nextPages = new Map(state.data?.pages ?? []);
      for (const archivedId of action.data.archived) {
        const page = nextPages.get(archivedId);
        if (page) {
          nextPages.set(archivedId, { ...page, archived: true });
        }
      }

      return {
        ...state,
        data: {
          ...state.data,
          pages: nextPages,
          urlMatches: state.data?.urlMatches ?? { urls: [], matches: new Map() },
        },
      };
    }
    case AppBackendRequestTypes.GetFunnelDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetHeatmapDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSegmentDetailViewSuccess:
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSidebarSuccess:
      return depsToState(action.data.deps, state);
    case PageRequestTypes.GetAllPagesRequest:
    case PageRequestTypes.GetAllPagesSuccess:
    case PageRequestTypes.GetAllPagesFailure:
      return pagesRequests(state, action);
  }
  return state;
};

const pageMetrics = createAppCacheReducer<ComputePageFlowResponse>()(
  PageRequestTypes.PageAppCacheIsLoading,
  PageRequestTypes.PageAppCacheLoadFailed,
  PageRequestTypes.PageAppCacheRefetching,
  PageRequestTypes.PageAppCacheHasData,
  PageRequestTypes.PageAppCacheFresh,
  PageRequestTypes.PageAppCacheSwap,
  PageRequestTypes.PageAppCacheClear,
)();

const getAllPageRequest = createRequestReducer<GetAllPagesResponse>()(
  PageRequestTypes.GetAllPagesRequest,
  PageRequestTypes.GetAllPagesSuccess,
  PageRequestTypes.GetAllPagesFailure,
);

type PagesReducerState = {
  pages: ReturnType<typeof pages>;
  pageMetrics: ReturnType<typeof pageMetrics>;
  getAllPageRequest: ReturnType<typeof getAllPageRequest>;
};

export const pagesReducer = combineReducers<PagesReducerState>({
  pages,
  pageMetrics,
  getAllPageRequest,
});
